import { Typography, Box, TextField, Button, Spinner } from "@kamstrup/kfl";
import { FC } from "react";
import { CenteredCard } from "../../containers/card";
import { useCodeVerification } from "../../hooks/useCodeVerification/useCodeVerification";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { commonStyles } from "../../styles/commonStyles";

interface InsertCodeStepProps {
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onCancel: () => Promise<void>;
  loading: boolean;
  resendLoading: boolean;
  codeError?: string;
}

export const InsertCodeStep: FC<InsertCodeStepProps> = ({
  onSubmit,
  onResend,
  onCancel,
  loading,
  codeError
}) => {
  const { code, error, handleCodeChange, isCodeValid } = useCodeVerification();
  const { translateString } = useTranslation();

  const handleSubmit = () => {
    if (!isCodeValid(code)) {
      return;
    }

    onSubmit(code);
  };

  const resendEmailText = (
    <Typography variant="body4" sx={commonStyles.introText}>
      {translateString("did_not_receive_email")}{" "}
      <Typography component="span" variant="body4" sx={commonStyles.linkText} onClick={onResend}>
        {translateString("resend_email")}
      </Typography>
    </Typography>
  );

  return (
    <CenteredCard title={translateString("verify_email")}>
      <Typography variant="body4" sx={commonStyles.introText}>
        {translateString("verify_email_description")}
      </Typography>

      <Box sx={commonStyles.textFieldContainer}>
        <TextField
          size="large"
          label={translateString("verification_code")}
          placeholder={translateString("enter_code")}
          fullWidth
          value={code}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            handleCodeChange(digitsOnly);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit();
            }
          }}
          helperText={error || codeError || ""}
          inputProps={{ maxLength: 6 }}
          autoFocus
        />
      </Box>

      <Box sx={commonStyles.buttonContainer}>
        <Button
          color="primary"
          startIcon={loading ? <Spinner /> : undefined}
          size="medium"
          variant="contained"
          fullWidth
          sx={commonStyles.button}
          onClick={handleSubmit}
          disabled={!isCodeValid(code) || loading}
        >
          {loading ? translateString("verifying") : translateString("confirm")}
        </Button>
      </Box>

      <Box sx={commonStyles.cancelButtonContainer}>
        <Typography
          sx={{
            ...commonStyles.linkText,
            color: "#EE2A20"
          }}
          variant="body3"
          onClick={onCancel}
        >
          {translateString("cancel")}
        </Typography>
      </Box>

      <Box sx={commonStyles.divider} />

      {resendEmailText}
    </CenteredCard>
  );
};
